import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { Week } from "../../../components/week-picker";
import { ValueLabelType } from "../../../types/others";
import {
  calAbsentTime,
  dateFormatter,
  listDatesInRange,
  setTimeForDate,
} from "../../../core/utils";
import {
  getUserDailyReportsState,
  getUserDailyReportsStateAction,
  ReportResponse,
} from "../../../redux/features/report-slice";
import { TimeOutlined } from "../../../components/icons/time-outlined";
import {
  UserResponse,
  UserWorkingTime,
} from "../../../redux/features/user-slice";
import {
  AbsentDate,
  AttendanceRecord,
  getAttendanceState,
  getAttendanceStateAction,
} from "../../../redux/features/misa-slice";
import { ArrowRightCircleOutlined } from "../../../components/icons/arrow-right-circle-outlined";
import { getErrorState } from "../../../redux/features/common-slice";
import ItemDailyReport from "../../daily-report/components/item-daily-report";
import Loading from "../../../components/loading";
import DateActions from "../../daily-report/components/date-actions";

interface Props {
  user: UserResponse;
  weekRange: Week;
  absentDates: AbsentDate[];
  setAbsentDates: (value: AbsentDate[]) => void;
}

export default function DailyReportTab(props: Props) {
  const { user, weekRange, absentDates, setAbsentDates } = props;

  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const userDailyReportsState = useSelector(getUserDailyReportsState);
  const attendance = useSelector(getAttendanceState).data;
  const errorState = useSelector(getErrorState);

  const [dates, setDates] = useState<ValueLabelType[]>([]);
  const [attendances, setAttendances] = useState<AttendanceRecord[]>([]);

  const fetchUserDailyReports = async () => {
    if (weekRange) {
      await dispatch(
        getUserDailyReportsStateAction({
          userId: user.id,
          start: dateFormatter(weekRange.firstDay, "yyyy-MM-dd"),
          end: dateFormatter(weekRange.lastDay, "yyyy-MM-dd"),
        })
      );
      await dispatch(
        getAttendanceStateAction({
          Filter: null,
          CustomFilter: null,
          QuickSearch: {},
          CustomParam: {
            SubSystemCode: "Attendance",
            FromDate: dateFormatter(weekRange.firstDay, "yyyy-MM-dd"),
            ToDate: dateFormatter(weekRange.lastDay, "yyyy-MM-dd"),
          },
        })
      );
    }
  };

  useEffect(() => {
    const tmpDates: AbsentDate[] = [];
    dates.map((date: ValueLabelType) => {
      let absentTime = 0;
      let attAbsentFrom = new Date();
      let attAbsentTo = new Date();
      let attType = "";
      let attReason = "";
      const currentDate = new Date(date.value);
      const breakStart: Date = setTimeForDate(currentDate, "12:00");
      const breakEnd: Date = setTimeForDate(currentDate, "13:00");

      if (user) {
        for (const workingTime of user.workingTimes) {
          const workStart = setTimeForDate(currentDate, workingTime.startTime);
          const workEnd = setTimeForDate(currentDate, workingTime.endTime);

          for (const attendance of attendances) {
            const absentFrom = new Date(attendance.FromDate);
            absentFrom.setSeconds(0, 0);
            const absentTo = new Date(attendance.ToDate);
            absentTo.setSeconds(0, 0);

            const absentHour = calAbsentTime(
              absentFrom,
              absentTo,
              workStart,
              workEnd,
              breakStart,
              breakEnd
            );

            if (absentHour > absentTime) {
              attType = attendance.AttendanceTypeName_EN;
              attReason = attendance.Reason;
              absentTime = absentHour;
              attAbsentFrom = absentFrom;
              attAbsentTo = absentTo;
            }
          }
        }
      }
      if (
        absentTime > 0 &&
        tmpDates.findIndex(
          (ad) =>
            ad.date === dateFormatter(date.value, "yyyy-MM-dd") &&
            ad.total === absentTime
        ) === -1
      ) {
        tmpDates.push({
          date: dateFormatter(date.value, "yyyy-MM-dd"),
          from: attAbsentFrom,
          to: attAbsentTo,
          total: absentTime,
          type: attType,
          reason: attReason,
        });
      }
    });

    setAbsentDates(tmpDates);
  }, [dates, user, attendances]);

  useEffect(() => {
    if (weekRange) {
      fetchUserDailyReports();
      setDates(listDatesInRange(weekRange.firstDay, weekRange.lastDay));
      setAbsentDates([]);
    }
  }, [weekRange]);

  useEffect(() => {
    if (attendance && user) {
      const data = attendance.Data.PageData;
      setAttendances(
        data.filter(
          (e: AttendanceRecord) =>
            e.EmployeeCode === user.misaCode && e.Status === 2
        )
      );
    }
  }, [attendance]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <div
      className="w-full flex space-x-4 overflow-auto"
      style={{
        height: "calc(100% - 58px)",
      }}
    >
      {userDailyReportsState.loading ? (
        <Loading exClassName="m-auto h-full w-4 content-center" />
      ) : (
        userDailyReportsState.data &&
        user &&
        dates.map((date: ValueLabelType) => {
          const dateStr = date.value.toISOString().split("T")[0];
          let attTypeName = "Absent";
          let absentTime = 0;
          const currentDate = new Date(date.value);
          const breakStart: Date = setTimeForDate(currentDate, "12:00");
          const breakEnd: Date = setTimeForDate(currentDate, "13:00");

          if (user) {
            for (const workingTime of user.workingTimes) {
              const workStart = setTimeForDate(
                currentDate,
                workingTime.startTime
              );
              const workEnd = setTimeForDate(currentDate, workingTime.endTime);

              for (const attendance of attendances) {
                const absentFrom = new Date(attendance.FromDate);
                absentFrom.setSeconds(0, 0);
                const absentTo = new Date(attendance.ToDate);
                absentTo.setSeconds(0, 0);

                const absentHour = calAbsentTime(
                  absentFrom,
                  absentTo,
                  workStart,
                  workEnd,
                  breakStart,
                  breakEnd
                );

                if (absentHour > absentTime) {
                  attTypeName = attendance.AttendanceTypeName_EN;
                  absentTime = absentHour;
                }
              }
            }
          }

          let totalDuration: number = 0;
          if (absentTime < 8) {
            const processedTags = new Set();
            if (
              userDailyReportsState.data &&
              userDailyReportsState.data[dateStr]
            ) {
              for (const report of userDailyReportsState.data[dateStr]) {
                if (report.tag === null || !processedTags.has(report.tag)) {
                  totalDuration += Number(report.duration);
                  if (report.tag !== null) {
                    processedTags.add(report.tag);
                  }
                }
              }
            }
          }

          return (
            <div
              key={date.value}
              className="flex flex-col h-full bg-background-default border border-divider rounded-lg p-2"
            >
              <div className="flex items-center justify-between mb-2 relative">
                {`${date.label} ${dateFormatter(date.value, "dd/MM/yyyy")}`}
                <div className="flex items-center justify-end space-x-2">
                  {absentTime < 8 && (
                    <label
                      title="Total Duration"
                      className={`w-fit flex items-center space-x-1 p-1 whitespace-nowrap ${
                        parseFloat(totalDuration.toFixed(2)) < 8
                          ? "text-error-dark"
                          : "text-success-main"
                      }`}
                    >
                      <TimeOutlined className="w-4 h-4" />
                      <label className="text-sm font-semibold">
                        {totalDuration > 0
                          ? totalDuration.toFixed(2)
                          : totalDuration}
                      </label>
                    </label>
                  )}
                  <DateActions
                    id={date.value}
                    absentTime={absentTime}
                    onImportFromPreviousDate={null}
                    onCopy={null}
                    onPaste={null}
                    onDelete={null}
                  />
                </div>
              </div>
              {absentTime === 8 ? (
                <div className="w-80 flex flex-col items-center justify-center flex-1 text-text-title">
                  <label>{attTypeName}</label>
                  <ArrowRightCircleOutlined className="h-5 w-5" />
                </div>
              ) : (
                <div className="w-80 overflow-auto">
                  {userDailyReportsState.data &&
                    userDailyReportsState.data[dateStr] &&
                    userDailyReportsState.data[dateStr].map(
                      (report: ReportResponse) => (
                        <ItemDailyReport
                          key={report.id}
                          report={report}
                          onClick={() => {}}
                          onCopy={null}
                        />
                      )
                    )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
